import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, FileText, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ONBOARDING_TASK_TYPE,
  ONBOARDING_TASK_TYPE_VALUES,
  OnboardingTask,
} from "@forgepoint/shared";
import { User, getUserDisplayName } from "@/store/teamsStore";
import { useOnboardingStore } from "@/modules/onboarding/onboardingStore";
import { parseEnumValue } from "@/lib/utils";

const UNASSIGNED = "unassigned";

interface TaskEditorRowProps {
  task: OnboardingTask;
  users: User[];
  isFirst: boolean;
  isLast: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onEditDescription: () => void;
}

/**
 * One editable board task: name and section save on blur, type and assignee
 * on change, plus a button opening the markdown description sheet. Completed
 * tasks show struck-through; check-off itself stays on the onboarding page.
 */
export function TaskEditorRow({
  task,
  users,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
  onEditDescription,
}: TaskEditorRowProps) {
  const editState = useOnboardingStore((state) =>
    state.taskEditStateById.get(task.id),
  );
  const updateTaskFields = useOnboardingStore(
    (state) => state.updateTaskFields,
  );
  const deleteTask = useOnboardingStore((state) => state.deleteTask);

  const [name, setName] = useState(task.name);
  const [section, setSection] = useState(task.section ?? "");

  useEffect(() => {
    setName(task.name);
    setSection(task.section ?? "");
  }, [task.name, task.section]);

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border p-2">
      <div className="flex flex-col">
        <Button
          variant="ghost"
          size="icon"
          className="h-5 w-5"
          disabled={isFirst}
          onClick={onMoveUp}
        >
          <ArrowUp className="h-3 w-3" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-5 w-5"
          disabled={isLast}
          onClick={onMoveDown}
        >
          <ArrowDown className="h-3 w-3" />
        </Button>
      </div>
      <Input
        className={`w-52 flex-1 ${task.completed ? "text-muted-foreground line-through" : ""}`}
        value={name}
        placeholder="Task name"
        onChange={(event) => setName(event.target.value)}
        onBlur={() => {
          if (name.trim() && name !== task.name) {
            updateTaskFields(task.id, { name: name.trim() });
          }
        }}
      />
      <Select
        value={task.type}
        onValueChange={(value) => {
          const parsed = parseEnumValue(ONBOARDING_TASK_TYPE_VALUES, value);
          if (parsed && parsed !== task.type) {
            updateTaskFields(task.id, { type: parsed });
          }
        }}
      >
        <SelectTrigger className="w-24">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ONBOARDING_TASK_TYPE.TASK}>Task</SelectItem>
          <SelectItem value={ONBOARDING_TASK_TYPE.UAT}>UAT</SelectItem>
        </SelectContent>
      </Select>
      <Input
        className="w-36"
        value={section}
        placeholder="Section"
        onChange={(event) => setSection(event.target.value)}
        onBlur={() => {
          if ((section.trim() || null) !== task.section) {
            updateTaskFields(task.id, { section: section.trim() || null });
          }
        }}
      />
      <Select
        value={task.assigneeUserId ?? UNASSIGNED}
        onValueChange={(value) => {
          const assigneeUserId = value === UNASSIGNED ? null : value;
          if (assigneeUserId !== task.assigneeUserId) {
            updateTaskFields(task.id, { assigneeUserId });
          }
        }}
      >
        <SelectTrigger className="w-40">
          <SelectValue placeholder="Assignee" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
          {users.map((user) => (
            <SelectItem key={user.id} value={user.id}>
              {getUserDisplayName(user)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="ml-auto flex items-center gap-1">
        {editState?.isSaving && (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        )}
        {editState?.error && (
          <span className="text-xs text-destructive">{editState.error}</span>
        )}
        <Button
          variant="ghost"
          size="icon"
          className={`h-7 w-7 ${task.description ? "text-primary" : "text-muted-foreground"}`}
          title={task.description ? "Edit description" : "Add description"}
          onClick={onEditDescription}
        >
          <FileText className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-destructive"
          onClick={() => deleteTask(task.id)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
